// Display order for stack rows: relevance descending, unscored last.
//
// This returns `{ supplement, index }` pairs rather than a sorted array of
// supplements, and that is the whole point. Every handler on a row — remove,
// the inline dose editor — addresses its supplement by its index in
// `selectedSupplements`, so sorting the array in place would silently rewire
// the × on the top row to whichever supplement used to be there. The pair
// keeps display order and identity separate.
//
// Array.prototype.sort is stable per spec, so supplements sharing a score keep
// the order the stack was built in. That matters more than usual here: today's
// backend emits integer relevance, so most stacks are entirely 4s and 5s and
// nearly every comparison is a tie.

export const relevanceOf = (supplement) => {
  const raw = supplement?.evidenceScore;
  // `Number(null)` and `Number('')` are both 0, so the empty cases have to be
  // rejected before coercion — otherwise an unscored supplement sorts as a
  // genuine zero and ties with one we actually rated 0.
  if (raw === null || raw === undefined || raw === '') return Number.NEGATIVE_INFINITY;
  const score = Number(raw);
  return Number.isFinite(score) ? score : Number.NEGATIVE_INFINITY;
};

export const orderByRelevance = (supplements) =>
  (supplements || [])
    .map((supplement, index) => ({ supplement, index }))
    .sort((a, b) => relevanceOf(b.supplement) - relevanceOf(a.supplement));

/**
 * Every row's React key, by its index in the member's list: the supplement id
 * AND which occurrence of that id the row is ("vitamin_d-0", "vitamin_d-1").
 *
 * The id alone is not unique — a member can enter the same supplement twice —
 * and React then warns "Encountered two children with the same key" and may
 * leave a row behind when the list changes. That is what happened when two
 * Vitamin D rows folded into one after an analysis: a stale pre-analysis row
 * stayed on screen beside the combined one (Tanu, 2026-09-25).
 *
 * Occurrence rather than list position, because position moves every row
 * after a fold or a removal: each one remounted, and a dose editor left open
 * on any of them closed and lost what was typed. With occurrences only the
 * duplicates themselves can change key.
 *
 * @param {Array<object>} supplements the member's rows, in their order
 * @returns {string[]} keys, indexed like `supplements`
 */
export const rowKeys = (supplements) => {
  const seen = new Map();
  return (supplements || []).map((supplement) => {
    const id = supplement?.supplementId || 'supplement';
    const occurrence = seen.get(id) || 0;
    seen.set(id, occurrence + 1);
    return `${id}-${occurrence}`;
  });
};

export default orderByRelevance;
